import React from 'react';
import { theme } from '../../styles/design-system';

const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  icon = null,
  iconPosition = 'left',
  className = '',
  onClick,
  type = 'button',
  ...props
}) => {
  const baseStyles = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing[2],
    border: 'none',
    borderRadius: theme.borderRadius.lg,
    fontWeight: theme.typography.fontWeight.medium,
    cursor: disabled || loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease-in-out',
    position: 'relative',
    overflow: 'hidden',
  };

  const sizeStyles = {
    sm: {
      padding: `${theme.spacing[2]} ${theme.spacing[3]}`,
      fontSize: theme.typography.fontSize.sm,
      minHeight: '32px',
    },
    md: {
      padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
      fontSize: theme.typography.fontSize.base,
      minHeight: '40px',
    },
    lg: {
      padding: `${theme.spacing[4]} ${theme.spacing[6]}`,
      fontSize: theme.typography.fontSize.lg,
      minHeight: '48px',
    },
  };

  const variantStyles = {
    primary: {
      backgroundColor: theme.colors.primary[500],
      color: 'white',
      '&:hover': {
        backgroundColor: theme.colors.primary[600],
        transform: 'translateY(-1px)',
        boxShadow: theme.shadows.lg,
      },
      '&:active': {
        transform: 'translateY(0)',
      },
    },
    secondary: {
      backgroundColor: theme.colors.secondary[500],
      color: 'white',
      '&:hover': {
        backgroundColor: theme.colors.secondary[600],
        transform: 'translateY(-1px)',
        boxShadow: theme.shadows.lg,
      },
    },
    outline: {
      backgroundColor: 'transparent',
      color: theme.colors.primary[500],
      border: `1px solid ${theme.colors.primary[500]}`,
      '&:hover': {
        backgroundColor: theme.colors.primary[50],
        transform: 'translateY(-1px)',
      },
    },
    ghost: {
      backgroundColor: 'transparent',
      color: theme.colors.gray[600],
      '&:hover': {
        backgroundColor: theme.colors.gray[100],
        color: theme.colors.gray[900],
      },
    },
    danger: {
      backgroundColor: theme.colors.error[500],
      color: 'white',
      '&:hover': {
        backgroundColor: theme.colors.error[600],
        transform: 'translateY(-1px)',
        boxShadow: theme.shadows.lg,
      },
    },
  };

  const disabledStyles = {
    opacity: 0.5,
    cursor: 'not-allowed',
    transform: 'none !important',
    boxShadow: 'none !important',
  };

  const loadingStyles = {
    position: 'relative',
    color: 'transparent',
  };

  const combinedStyles = {
    ...baseStyles,
    ...sizeStyles[size],
    ...variantStyles[variant],
    ...(disabled || loading ? disabledStyles : {}),
    ...(loading ? loadingStyles : {}),
  };

  const { style: customStyle, ...otherProps } = props;

  return (
    <button
      type={type}
      className={className}
      style={{ ...combinedStyles, ...customStyle }}
      onClick={onClick}
      disabled={disabled || loading}
      {...otherProps}
    >
      {loading && (
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '16px',
            height: '16px',
            border: '2px solid transparent',
            borderTop: '2px solid currentColor',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
      )}

      {!loading && icon && iconPosition === 'left' && (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {icon}
        </span>
      )}

      {!loading && children}

      {!loading && icon && iconPosition === 'right' && (
        <span style={{ display: 'flex', alignItems: 'center' }}>
          {icon}
        </span>
      )}

      <style>{`
        @keyframes spin {
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
      `}</style>
    </button>
  );
};

export default Button;
